import React, { useContext, useEffect, useMemo } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

import FilterTag from '@components/atoms/filter-tag/filterTag';
import Input from '@components/atoms/input/input';
import CloseIcon from '@components/icons/close.svg';
import { Context } from '../../../context/provider';

const FilterInput = ({ searchTerms = [] }) => {
  const t = useTranslations();
  const { filter, setSelectedSearchTerm, setShowSearchTerms } = useContext(Context);
  const { query, clear, refine } = useSearchBox();

  const terms = useMemo(() => [...new Set(searchTerms.map(i => i.searchTerm))], [searchTerms]);

  const onClearInput = () => {
    setSelectedSearchTerm(undefined);
    clear();
  };

  const onSearchFocus = () => {
    setShowSearchTerms(true);
  };

  const onFilterTagClick = term => {
    setSelectedSearchTerm(filter.selectedSearchTerm === term ? undefined : term);
  };

  useEffect(() => {
    refine(filter.selectedSearchTerm || '');
  }, [filter.selectedSearchTerm]);

  return (
    <>
      <div className="input-wrapper">
        <Input
          className={`search search--${filter.selectedSearchTerm ? 'hasTerm' : 'noTerm'}`}
          icon={filter.selectedSearchTerm || query ? CloseIcon : null}
          variant="simple"
          name="search"
          label={t('archive.searchLabel')}
          placeholder={t('archive.searchPlaceholder')}
          value={filter.selectedSearchTerm || query}
          onChange={e => refine(e.target.value)}
          onFocus={onSearchFocus}
          onIconClick={onClearInput}
        />

        {filter.selectedSearchTerm && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="tags"
          >
            <FilterTag
              border
              onClick={() => onFilterTagClick(filter.selectedSearchTerm)}
              inverted
              role="button"
            >
              {filter.selectedSearchTerm}
            </FilterTag>
          </motion.div>
        )}
      </div>

      {filter.showSearchTerms && (
        <motion.div
          initial={{ opacity: 0, maxHeight: 0 }}
          animate={{ opacity: 1, maxHeight: 1000 }}
          exit={{ opacity: 0, maxHeight: 0 }}
          className="tags"
        >
          {terms.map(term => (
            <motion.div key={term}>
              <FilterTag
                border
                inverted={term === filter.selectedSearchTerm}
                onClick={() => onFilterTagClick(term)}
                role="button"
              >
                {term}
              </FilterTag>
            </motion.div>
          ))}
        </motion.div>
      )}
    </>
  );
};

FilterInput.propTypes = {
  searchTerms: PropTypes.arrayOf(PropTypes.shape())
};

export default FilterInput;
